import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { theme } from '../styles/theme';
import {
  iosNativeCenterHeaderLeft,
  iosNativeContent,
  iosNativePageRoot,
  iosNativeScreenSubtitle,
  iosNativeScreenTitle,
} from '../styles/iosNativePageCss';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { IngredientModal } from '../components/IngredientModal';
import { products, getProductsByTags, ProductTag as ProductTagType } from '../data/products';
import { ingredientLibrary, getIngredientByName, IngredientInfo } from '../data/ingredients';
import { useToast } from '../components/ui/Toast';
import { useSubscription } from '../hooks/useSubscription';
import { Sparkles, Filter, X, DollarSign, ArrowLeft, Clock, Sun, Moon, AlertTriangle, Loader } from 'lucide-react';
import { useLatestScan } from '../hooks/useLatestScan';
import { useAWSAuth as useAuth } from '../contexts/AWSAuthContext';
import { getRankedProducts, RankedProduct } from '../services/productRankingService';

const PageContainer = styled.div`
  min-height: calc(100vh - 72px);
  background: linear-gradient(180deg, ${theme.colors.background.secondary} 0%, ${theme.colors.background.primary} 100%);
  padding: 2rem 1rem 4rem;

  ${iosNativePageRoot}
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;

  ${iosNativeContent}
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 3rem;
  animation: slideUp 0.5s ease-out;
  position: relative;

  ${iosNativeCenterHeaderLeft}
`;

const BackButton = styled(Button)`
  margin-bottom: 1.5rem;
`;

const Title = styled.h1`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: ${theme.typography.fontSize['4xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.text.primary};
  margin-bottom: 0.5rem;

  ${iosNativeScreenTitle}
`;

const Subtitle = styled.p`
  font-size: ${theme.typography.fontSize.lg};
  color: ${theme.colors.text.secondary};
  max-width: 600px;
  margin: 0 auto;

  body.native-app & {
    margin-left: 0;
  }

  ${iosNativeScreenSubtitle}
`;

const FilterSection = styled.div`
  margin-bottom: 2rem;
  opacity: 1 !important;
  will-change: auto;
  transform: translateZ(0);
  backface-visibility: hidden;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
`;

const FilterLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: ${theme.typography.fontSize.base};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: #1a1a1a;
  margin-bottom: 1rem;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
  will-change: auto;
  transform: translateZ(0);
  backface-visibility: hidden;

  svg {
    color: ${theme.colors.primary[600]};
    filter: none !important;
    transform: translateZ(0);
  }
`;

const FilterChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

const FilterChip = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  border: 2px solid ${props => props.$active ? theme.colors.primary[500] : theme.colors.border.medium || theme.colors.border.light};
  background: ${props => props.$active ? theme.colors.primary[500] : '#ffffff'};
  color: ${props => props.$active ? '#ffffff' : '#1a1a1a'};
  cursor: pointer;
  transition: border-color 0.2s ease, background-color 0.2s ease, transform 0.2s ease;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
  will-change: auto;
  backface-visibility: hidden;
  filter: none !important;
  opacity: 1 !important;
  transform: translateZ(0);

  &:hover {
    border-color: ${theme.colors.primary[500]};
    transform: translateY(-2px) translateZ(0);
  }

  span {
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-rendering: optimizeLegibility;
    filter: none !important;
  }

  svg {
    width: 16px;
    height: 16px;
    filter: none !important;
    transform: translateZ(0);
  }
`;

const SectionHeader = styled.div`
  margin: 3rem 0 1.5rem;
`;

const SectionTitle = styled.h2`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.text.primary};
  margin-bottom: 0.5rem;
`;

const SectionSubtitle = styled.p`
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.text.secondary};
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  margin-bottom: 2rem;

  @media (min-width: ${theme.breakpoints.sm}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: ${theme.breakpoints.lg}) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const ProductCard = styled(Card)`
  display: flex;
  flex-direction: column;
  animation: slideUp 0.4s ease-out both;
`;

const ProductHeader = styled.div`
  margin-bottom: 1rem;
`;

const BrandName = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.primary[600]};
  font-weight: ${theme.typography.fontWeight.semibold};
  margin-bottom: 0.25rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const ProductName = styled.h3`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: ${theme.typography.fontSize.lg};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.text.primary};
  margin: 0;
`;

const ProductTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 0.75rem 0;
`;

const ProductTag = styled.span`
  padding: 0.25rem 0.5rem;
  background: ${theme.colors.glow.mint};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.mint[700]};
  font-weight: ${theme.typography.fontWeight.medium};
`;

const Ingredients = styled.div`
  margin: 1rem 0;
`;

const IngredientsTitle = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.text.tertiary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.5rem;
`;

const IngredientChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
`;

const IngredientChip = styled.button`
  padding: 0.25rem 0.625rem;
  background: white;
  border: 1px solid ${theme.colors.border.medium};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.text.secondary};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${theme.colors.primary[400]};
    background: ${theme.colors.glow.mint};
    color: ${theme.colors.primary[700]};
  }
`;

const Benefits = styled.p`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.secondary};
  line-height: 1.6;
  margin: 1rem 0;
  flex: 1;
`;

const RankedProductExplanation = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.secondary};
  line-height: 1.6;
  margin: 0.75rem 0;
  padding: 0.75rem;
  background: ${theme.colors.glow.mint}20;
  border-left: 3px solid ${theme.colors.primary[500]};
  border-radius: ${theme.borderRadius.md};
`;

const UsageTimingBadge = styled.div<{ $timing: 'AM' | 'PM' | 'AM/PM' | 'As needed' }>`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  background: ${props => {
    if (props.$timing === 'AM') return '#fef3c7';
    if (props.$timing === 'PM') return '#e0e7ff';
    if (props.$timing === 'AM/PM') return '#dcfce7';
    return '#f3f4f6';
  }};
  color: ${props => {
    if (props.$timing === 'AM') return '#92400e';
    if (props.$timing === 'PM') return '#3730a3';
    if (props.$timing === 'AM/PM') return '#166534';
    return '#374151';
  }};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.semibold};
  margin-top: 0.5rem;
`;

const SafetyNotes = styled.div`
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid ${theme.colors.border.light};
`;

const SafetyNote = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.status.warning};
  margin-top: 0.5rem;
  
  svg {
    flex-shrink: 0;
    margin-top: 0.125rem;
  }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 3rem 2rem;
  color: ${theme.colors.text.secondary};
  
  svg {
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const RankingError = styled.div`
  padding: 1rem;
  background: ${theme.colors.status.error}10;
  border: 1px solid ${theme.colors.status.error}30;
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.status.error};
  font-size: ${theme.typography.fontSize.sm};
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ProductFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: auto;
  padding-top: 1rem;
  border-top: 1px solid ${theme.colors.border.light};
`;

const PriceIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  color: ${theme.colors.text.secondary};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  
  svg {
    width: 64px;
    height: 64px;
    color: ${theme.colors.text.tertiary};
    margin-bottom: 1rem;
  }
  
  h3 {
    font-size: ${theme.typography.fontSize.xl};
    color: ${theme.colors.text.primary};
    margin-bottom: 0.5rem;
  }
  
  p {
    color: ${theme.colors.text.secondary};
  }
`;

const filterOptions: { tag: ProductTagType; label: string; emoji: string }[] = [
  { tag: 'k-beauty', label: 'K-Beauty', emoji: '🇰🇷' },
  { tag: 'influencer', label: 'Influencer Picks', emoji: '⭐' },
  { tag: 'budget', label: 'Budget', emoji: '💰' },
  { tag: 'student-pick', label: 'Student Essentials', emoji: '🎓' },
  { tag: 'natural', label: 'Natural', emoji: '🌿' },
  { tag: 'gentle', label: 'Gentle', emoji: '🤲' },
  { tag: 'hydrating', label: 'Hydrating', emoji: '💧' },
  { tag: 'barrier-repair', label: 'Barrier Repair', emoji: '🛡️' },
  { tag: 'acne-control', label: 'Acne Control', emoji: '🎯' },
  { tag: 'brightening', label: 'Brightening', emoji: '✨' },
  { tag: 'anti-inflammatory', label: 'Calming', emoji: '🍃' },
];

const ProductsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { tier } = useSubscription();
  const { userProfile } = useAuth();
  const { latestScan, loading: scanLoading } = useLatestScan();
  const [activeFilters, setActiveFilters] = useState<ProductTagType[]>([]);
  const [selectedIngredient, setSelectedIngredient] = useState<IngredientInfo | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { showToast } = useToast();
  const [rankedProducts, setRankedProducts] = useState<RankedProduct[]>([]);
  const [rankingLoading, setRankingLoading] = useState(false);
  const [rankingError, setRankingError] = useState<string | null>(null);
  
  // Check if user came from results page (Pro/Elite only)
  const showBackButton = (tier === 'pro' || tier === 'elite') && 
                         (location.state?.fromResults === true);
  
  // Fetch ranked products when scan data is available and no filters are active
  useEffect(() => {
    const fetchRankedProducts = async () => {
      // Only rank if:
      // 1. User has a scan with analysis
      // 2. No filters are active (filters override ranking)
      // 3. Not already loading
      if (activeFilters.length > 0 || rankingLoading || !latestScan?.analysis) {
        return;
      }
      
      setRankingLoading(true);
      setRankingError(null);
      
      try {
        const ranked = await getRankedProducts({
          scanRecord: latestScan,
          candidateProducts: products,
          userGender: userProfile?.gender,
          limit: 12, // Show top 12 ranked products
        });
        
        setRankedProducts(ranked);
      } catch (error) {
        console.error('Error ranking products:', error);
        setRankingError(error instanceof Error ? error.message : 'Failed to load personalized recommendations');
        // Don't show toast, just show error message in UI
      } finally {
        setRankingLoading(false);
      }
    };
    
    void fetchRankedProducts();
  }, [latestScan?.scanId, latestScan?.analysis, activeFilters.length, userProfile?.gender]);

  const toggleFilter = (tag: ProductTagType) => {
    setActiveFilters(prev =>
      prev.includes(tag)
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const clearFilters = () => {
    setActiveFilters([]);
  };

  const handleIngredientClick = (ingredientName: string) => {
    // Try to find ingredient with improved matching
    const ingredient = getIngredientByName(ingredientName);
    if (ingredient) {
      setSelectedIngredient(ingredient);
      setIsModalOpen(true);
    } else {
      // If ingredient not found, try to find a partial match or show helpful message
      const partialMatch = ingredientLibrary.find(ing => 
        ingredientName.toLowerCase().split(/\s+/).some(word => 
          word.length > 3 && ing.name.toLowerCase().includes(word)
        )
      );
      
      if (partialMatch) {
        setSelectedIngredient(partialMatch);
        setIsModalOpen(true);
      } else {
        // Show toast notification
        showToast(`Ingredient information for "${ingredientName}" is coming soon!`, 'info');
      }
    }
  };

  const filteredProducts = activeFilters.length > 0
    ? getProductsByTags(activeFilters)
    : products;

  // When filters are active, show only filtered products in a unified list
  // When no filters, show grouped by category
  const kbeautyProducts = activeFilters.length > 0 
    ? [] // Don't show category sections when filtering
    : filteredProducts.filter(p => p.tags.includes('k-beauty'));
  
  const influencerProducts = activeFilters.length > 0
    ? [] // Don't show category sections when filtering
    : filteredProducts.filter(p => p.tags.includes('influencer'));
  
  const budgetProducts = activeFilters.length > 0
    ? [] // Don't show category sections when filtering
    : filteredProducts.filter(p => p.tags.includes('budget') || p.tags.includes('student-pick'));

  return (
    <PageContainer>
      <Container>
        {showBackButton && (
          <BackButton
            variant="outline"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft size={16} />
            Back
          </BackButton>
        )}
        <Header>
          <Title>Curated Product Picks ✨</Title>
          <Subtitle>
            {latestScan?.analysis && activeFilters.length === 0
              ? 'AI-powered personalized recommendations based on your skin scan'
              : 'Ingredient-science backed recommendations for your skin goals'}
          </Subtitle>
        </Header>

        {rankingError && (
          <RankingError>
            <AlertTriangle size={16} />
            <span>{rankingError}</span>
          </RankingError>
        )}

        <FilterSection data-filter-section>
          <FilterLabel>
            <Filter size={18} />
            Filter by Category
          </FilterLabel>
          <FilterChips>
            {filterOptions.map(option => (
              <FilterChip
                key={option.tag}
                $active={activeFilters.includes(option.tag)}
                onClick={() => toggleFilter(option.tag)}
              >
                <span>{option.emoji}</span>
                {option.label}
              </FilterChip>
            ))}
            {activeFilters.length > 0 && (
              <FilterChip
                $active={false}
                onClick={clearFilters}
                style={{ borderColor: theme.colors.status.error, color: theme.colors.status.error }}
              >
                <X size={16} />
                Clear All
              </FilterChip>
            )}
          </FilterChips>
        </FilterSection>

        {/* PERSONALIZED RANKED PRODUCTS SECTION - Show when scan data is available and no filters */}
        {latestScan?.analysis && activeFilters.length === 0 && (
          <>
            {rankingLoading ? (
              <LoadingState>
                <Loader size={32} style={{ margin: '0 auto 1rem' }} />
                <p>Analyzing your skin scan and ranking products...</p>
              </LoadingState>
            ) : rankedProducts.length > 0 ? (
              <>
                <SectionHeader>
                  <SectionTitle>✨ Personalized Recommendations</SectionTitle>
                  <SectionSubtitle>
                    Top products ranked for your specific skin type and concerns
                  </SectionSubtitle>
                </SectionHeader>

                <ProductGrid>
                  {rankedProducts.map((product, index) => (
                    <ProductCard key={product.id} hover padding="lg" style={{ animationDelay: `${index * 0.05}s` }}>
                      <ProductHeader>
                        <BrandName>{product.brand}</BrandName>
                        <ProductName>{product.productCategory}</ProductName>
                      </ProductHeader>

                      <ProductTags>
                        {product.tags.slice(0, 3).map(tag => (
                          <ProductTag key={tag}>{tag.replace('-', ' ')}</ProductTag>
                        ))}
                      </ProductTags>

                      <RankedProductExplanation>
                        <strong>Why this works for you:</strong> {product.explanation}
                      </RankedProductExplanation>

                      <UsageTimingBadge $timing={product.usageTiming}>
                        {product.usageTiming === 'AM' && <Sun size={14} />}
                        {product.usageTiming === 'PM' && <Moon size={14} />}
                        {product.usageTiming === 'AM/PM' && <Clock size={14} />}
                        {product.usageTiming === 'As needed' && <Clock size={14} />}
                        {product.usageTiming}
                      </UsageTimingBadge>

                      {product.safetyNotes && product.safetyNotes.length > 0 && (
                        <SafetyNotes>
                          {product.safetyNotes.map((note, idx) => (
                            <SafetyNote key={idx}>
                              <AlertTriangle size={14} />
                              <span>{note}</span>
                            </SafetyNote>
                          ))}
                        </SafetyNotes>
                      )}

                      <Ingredients>
                        <IngredientsTitle>Key Ingredients</IngredientsTitle>
                        <IngredientChips>
                          {product.keyIngredients.map((ing, idx) => (
                            <IngredientChip
                              key={idx}
                              onClick={() => handleIngredientClick(ing)}
                            >
                              {ing}
                            </IngredientChip>
                          ))}
                        </IngredientChips>
                      </Ingredients>

                      <Benefits>{product.ingredientBenefits}</Benefits>

                      <ProductFooter>
                        <PriceIndicator>
                          <DollarSign size={16} />
                          {product.price || product.priceRange}
                        </PriceIndicator>
                        <Button 
                          size="sm" 
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (product.keyIngredients && product.keyIngredients.length > 0) {
                              handleIngredientClick(product.keyIngredients[0]);
                            }
                          }}
                        >
                          <Sparkles size={14} />
                          Learn More
                        </Button>
                      </ProductFooter>
                    </ProductCard>
                  ))}
                </ProductGrid>
              </>
            ) : null}
          </>
        )}

        {/* K-BEAUTY SECTION - Show when no scan data or filters are active */}
        {(!latestScan?.analysis || activeFilters.length > 0) && kbeautyProducts.length > 0 && (
          <>
            <SectionHeader>
              <SectionTitle>🇰🇷 K-Beauty Glow Picks</SectionTitle>
              <SectionSubtitle>
                Gentle, effective formulas from Korean skincare innovation
              </SectionSubtitle>
            </SectionHeader>

            <ProductGrid>
              {kbeautyProducts.map((product, index) => (
                <ProductCard key={product.id} hover padding="lg" style={{ animationDelay: `${index * 0.05}s` }}>
                  <ProductHeader>
                    <BrandName>{product.brand}</BrandName>
                    <ProductName>{product.productCategory}</ProductName>
                  </ProductHeader>

                  <ProductTags>
                    {product.tags.slice(0, 3).map(tag => (
                      <ProductTag key={tag}>{tag.replace('-', ' ')}</ProductTag>
                    ))}
                  </ProductTags>

                  <Ingredients>
                    <IngredientsTitle>Key Ingredients</IngredientsTitle>
                    <IngredientChips>
                      {product.keyIngredients.map((ing, idx) => (
                        <IngredientChip
                          key={idx}
                          onClick={() => handleIngredientClick(ing)}
                        >
                          {ing}
                        </IngredientChip>
                      ))}
                    </IngredientChips>
                  </Ingredients>

                  <Benefits>{product.ingredientBenefits}</Benefits>

                  <ProductFooter>
                    <PriceIndicator>
                      <DollarSign size={16} />
                      {product.price || product.priceRange}
                    </PriceIndicator>
                    <Button 
                      size="sm" 
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (product.keyIngredients && product.keyIngredients.length > 0) {
                          handleIngredientClick(product.keyIngredients[0]);
                        }
                      }}
                    >
                      <Sparkles size={14} />
                      Learn More
                    </Button>
                  </ProductFooter>
                </ProductCard>
              ))}
            </ProductGrid>
          </>
        )}

        {/* INFLUENCER SECTION */}
        {influencerProducts.length > 0 && (
          <>
            <SectionHeader>
              <SectionTitle>⭐ Influencer Favorites</SectionTitle>
              <SectionSubtitle>
                Clean beauty from trusted creators — science-backed formulas
              </SectionSubtitle>
            </SectionHeader>

            <ProductGrid>
              {influencerProducts.map((product, index) => (
                <ProductCard key={product.id} hover padding="lg" style={{ animationDelay: `${index * 0.05}s` }}>
                  <ProductHeader>
                    <BrandName>⭐ {product.brand}</BrandName>
                    <ProductName>{product.productCategory}</ProductName>
                  </ProductHeader>

                  <ProductTags>
                    {product.tags.slice(0, 3).map(tag => (
                      <ProductTag key={tag}>{tag.replace('-', ' ')}</ProductTag>
                    ))}
                  </ProductTags>

                  <Ingredients>
                    <IngredientsTitle>Key Ingredients</IngredientsTitle>
                    <IngredientChips>
                      {product.keyIngredients.map((ing, idx) => (
                        <IngredientChip
                          key={idx}
                          onClick={() => handleIngredientClick(ing)}
                        >
                          {ing}
                        </IngredientChip>
                      ))}
                    </IngredientChips>
                  </Ingredients>

                  <Benefits>{product.ingredientBenefits}</Benefits>

                  <ProductFooter>
                    <PriceIndicator>
                      <DollarSign size={16} />
                      {product.price || product.priceRange}
                    </PriceIndicator>
                    <Button 
                      size="sm" 
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (product.keyIngredients && product.keyIngredients.length > 0) {
                          handleIngredientClick(product.keyIngredients[0]);
                        }
                      }}
                    >
                      <Sparkles size={14} />
                      Learn More
                    </Button>
                  </ProductFooter>
                </ProductCard>
              ))}
            </ProductGrid>
          </>
        )}

        {/* BUDGET SECTION */}
        {budgetProducts.length > 0 && (
          <>
            <SectionHeader>
              <SectionTitle>💰 Budget Student Essentials</SectionTitle>
              <SectionSubtitle>
                High-quality formulas that won't break the bank
              </SectionSubtitle>
            </SectionHeader>

            <ProductGrid>
              {budgetProducts.map((product, index) => (
                <ProductCard key={product.id} hover padding="lg" style={{ animationDelay: `${index * 0.05}s` }}>
                  <ProductHeader>
                    <BrandName>💰 {product.brand}</BrandName>
                    <ProductName>{product.productCategory}</ProductName>
                  </ProductHeader>

                  <ProductTags>
                    {product.tags.slice(0, 3).map(tag => (
                      <ProductTag key={tag}>{tag.replace('-', ' ')}</ProductTag>
                    ))}
                  </ProductTags>

                  <Ingredients>
                    <IngredientsTitle>Key Ingredients</IngredientsTitle>
                    <IngredientChips>
                      {product.keyIngredients.map((ing, idx) => (
                        <IngredientChip
                          key={idx}
                          onClick={() => handleIngredientClick(ing)}
                        >
                          {ing}
                        </IngredientChip>
                      ))}
                    </IngredientChips>
                  </Ingredients>

                  <Benefits>{product.ingredientBenefits}</Benefits>

                  <ProductFooter>
                    <PriceIndicator>
                      <DollarSign size={16} />
                      {product.price || product.priceRange}
                    </PriceIndicator>
                    <Button 
                      size="sm" 
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (product.keyIngredients && product.keyIngredients.length > 0) {
                          handleIngredientClick(product.keyIngredients[0]);
                        }
                      }}
                    >
                      <Sparkles size={14} />
                      Learn More
                    </Button>
                  </ProductFooter>
                </ProductCard>
              ))}
            </ProductGrid>
          </>
        )}

        {/* FILTERED PRODUCTS SECTION - Show when filters are active */}
        {activeFilters.length > 0 && filteredProducts.length > 0 && (
          <>
            <SectionHeader>
              <SectionTitle>
                {activeFilters.map(tag => {
                  const option = filterOptions.find(opt => opt.tag === tag);
                  return option ? `${option.emoji} ${option.label}` : tag;
                }).join(' • ')}
              </SectionTitle>
              <SectionSubtitle>
                {filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''} found
              </SectionSubtitle>
            </SectionHeader>

            <ProductGrid>
              {filteredProducts.map((product, index) => (
                <ProductCard key={product.id} hover padding="lg" style={{ animationDelay: `${index * 0.05}s` }}>
                  <ProductHeader>
                    <BrandName>{product.brand}</BrandName>
                    <ProductName>{product.productCategory}</ProductName>
                  </ProductHeader>

                  <ProductTags>
                    {product.tags.slice(0, 3).map(tag => (
                      <ProductTag key={tag}>{tag.replace('-', ' ')}</ProductTag>
                    ))}
                  </ProductTags>

                  <Ingredients>
                    <IngredientsTitle>Key Ingredients</IngredientsTitle>
                    <IngredientChips>
                      {product.keyIngredients.map((ing, idx) => (
                        <IngredientChip
                          key={idx}
                          onClick={() => handleIngredientClick(ing)}
                        >
                          {ing}
                        </IngredientChip>
                      ))}
                    </IngredientChips>
                  </Ingredients>

                  <Benefits>{product.ingredientBenefits}</Benefits>

                  <ProductFooter>
                    <PriceIndicator>
                      <DollarSign size={16} />
                      {product.price || product.priceRange}
                    </PriceIndicator>
                    <Button 
                      size="sm" 
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (product.keyIngredients && product.keyIngredients.length > 0) {
                          handleIngredientClick(product.keyIngredients[0]);
                        }
                      }}
                    >
                      <Sparkles size={14} />
                      Learn More
                    </Button>
                  </ProductFooter>
                </ProductCard>
              ))}
            </ProductGrid>
          </>
        )}

        {activeFilters.length > 0 && filteredProducts.length === 0 && (
          <EmptyState>
            <Filter size={64} />
            <h3>No products found</h3>
            <p>Try adjusting your filters to see more products</p>
            <Button onClick={clearFilters} style={{ marginTop: '1rem' }}>
              Clear Filters
            </Button>
          </EmptyState>
        )}
      </Container>

      <IngredientModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        ingredient={selectedIngredient}
      />
    </PageContainer>
  );
};

export default ProductsPage;
